import React, {useState} from 'react';
import {Empty, Spin, Tag} from 'antd';
import {DownOutlined, RightOutlined} from '@ant-design/icons';
import {useLazyQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {GET_MOBILE_COMPLAINT_TASKS, MobileComplaintTasksResponse} from 'graphql/complaintTask/queries';
import {ComplaintTaskRow} from 'graphql/complaintTask/types';
import {
  formatDuration,
  formatTime,
  PRIORITY_COLOR,
  priorityLabel,
  subTypeLabel,
} from 'pages/qualitycontrol/complaintTask/complaintTaskUtils';
import {byUrgency, countdownOf} from '../mobileQueue';

/** 列表时效徽标（PRD：响应超时黄 / 处理超时红 / 正常绿）；不考核不显示。 */
const TimelinessBadge: React.FC<{row: ComplaintTaskRow; nowMs: number}> = ({row, nowMs}) => {
  const {t} = useTranslation();
  const c = countdownOf(row, nowMs);
  const duration = formatDuration(c.ms ?? 0);
  if (c.tone === 'resolved') return <Tag className="!mr-0">{formatTime(row.finishAt)}</Tag>;
  if (c.tone === 'none') return null;
  if (c.tone === 'overdue') {
    return row.actualStartAt
      ? <Tag color="red" className="!mr-0">{t('complaintMobile.countdown.resolveOverdue', {duration})}</Tag>
      : <Tag color="gold" className="!mr-0">{t('complaintMobile.countdown.responseOverdue', {duration})}</Tag>;
  }
  return <Tag color="green" className="!mr-0">{t(`complaintMobile.countdown.${c.tone}`, {duration})}</Tag>;
};

const TaskItem: React.FC<{row: ComplaintTaskRow; nowMs: number; onOpen: (id: number) => void}> = ({row, nowMs, onOpen}) => {
  const {t} = useTranslation();
  return (
    <button type="button" className="w-full text-left bg-white rounded-lg p-3 flex flex-col gap-1 active:bg-gray-50"
            onClick={() => onOpen(row.taskId)}>
      <div className="flex justify-between items-center gap-2">
        <div className="flex gap-1 items-center">
          <Tag color={PRIORITY_COLOR[row.priority ?? 3]} className="!mr-0">{priorityLabel(row.priority)}</Tag>
          {row.status === 40 && (
            <span className="text-xs text-gray-400">
              {row.actualStartAt ? t('complaintMobile.list.stageInProgress') : t('complaintMobile.list.stageUnresponded')}
            </span>
          )}
        </div>
        <TimelinessBadge row={row} nowMs={nowMs}/>
      </div>
      <div className="text-sm font-medium">
        {row.customerLevel && <Tag className="!mr-1">{row.customerLevel}</Tag>}
        {row.customerName ?? '—'}
      </div>
      <div className="text-sm text-gray-600 line-clamp-2">{row.title}</div>
      <div className="text-xs text-gray-400">{subTypeLabel(row.subType, row.subTypeName)}</div>
    </button>
  );
};

/**
 * 全部列表视图（PRD 手机版）：待处理按剩余时效排序；底部「已解决（近 30 天）」点开再查；
 * 点任意一条回到逐条模式并停在这条。
 */
const MobileTaskList: React.FC<{pending: ComplaintTaskRow[]; nowMs: number; onOpen: (id: number) => void}> = ({
  pending, nowMs, onOpen,
}) => {
  const {t} = useTranslation();
  const [resolvedOpen, setResolvedOpen] = useState(false);
  const [loadResolved, {data, loading}] = useLazyQuery<MobileComplaintTasksResponse>(GET_MOBILE_COMPLAINT_TASKS, {
    variables: {stage: 'RESOLVED'},
    fetchPolicy: 'network-only',
  });
  const resolved = data?.mobileComplaintTasks.list ?? [];
  const sorted = [...pending].sort(byUrgency);

  const toggleResolved = () => {
    if (!resolvedOpen) void loadResolved();
    setResolvedOpen((v) => !v);
  };

  return (
    <div className="px-3 py-3 flex flex-col gap-2 pb-8">
      <div className="text-sm text-gray-500 px-1">{t('complaintMobile.list.pending', {count: sorted.length})}</div>
      {sorted.length === 0
        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('complaintMobile.list.empty')}/>
        : sorted.map((r) => <TaskItem key={r.taskId} row={r} nowMs={nowMs} onOpen={onOpen}/>)}

      <button type="button" className="mt-3 px-1 text-sm text-gray-500 flex items-center gap-1" onClick={toggleResolved}>
        {resolvedOpen ? <DownOutlined/> : <RightOutlined/>}
        {t('complaintMobile.list.resolved')}
        {resolvedOpen && !loading && ` · ${resolved.length}`}
      </button>
      {resolvedOpen && (
        loading ? <div className="py-6 text-center"><Spin/></div>
          : resolved.length === 0
            ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('complaintMobile.list.resolvedEmpty')}/>
            : resolved.map((r) => <TaskItem key={r.taskId} row={r} nowMs={nowMs} onOpen={onOpen}/>)
      )}
    </div>
  );
};

export default MobileTaskList;
